import { getLogger } from '@logtape/logtape';
import { RTNode } from '@prismicio/client';

import { EnrichedLocation } from '@/models/EnrichedPrismicDocuments.ts';
import { EditPatientFormData, PatientFormData } from '@/models/Patient.ts';
import { Appointment, DailyAppointment } from '@/models/appointment/Appointment.ts';
import { PVSProvider } from '@/models/customers/PVSProvider.ts';
import { DocumentType } from '@/models/documents/Document.ts';
import { DocumentCodeErrorResponse } from '@/models/documents/document-error-dto.ts';
import FormattedForm from '@/models/forms/FormattedForm.ts';
import {
    FormKeyObject,
    FormKeyObject as FormTemplateAppointment,
} from '@/models/forms/backend-types/FormDataAppointment.ts';
import { FormDataCode, FormDataCodeDto, PurePersonalData } from '@/models/forms/backend-types/FormDataCode.ts';
import { FormDataRequiredTemplate } from '@/models/forms/backend-types/FormDataRequiredTemplates.ts';
import { GetDocumentsResult } from '@/models/forms/backend-types/GetDocumentsResult.ts';
import { FormTemplate, FormTemplateContent } from '@/models/forms/backend-types/form-template/FormTemplate.ts';
import { FormsFetch, FormsFetchStatus, FormsResolution, FormsSkipReason } from '@/models/forms/forms-fetch.ts';
import { InsuranceType } from '@/models/insurance.ts';
import { MedicalRecordsDocument, MedicalRecordsDocumentDataTypeOfDocResponseItem } from '@/models/prismicTypes.ts';

import { getDocumentDownloadUrl } from '@/services/documentsService.ts';
import { showErrorNotification } from '@/services/notificationService.ts';

import { useConfigStore } from '@/stores/configStore.ts';
import { useFormStore } from '@/stores/formStore.ts';

import { getInstanceId, getPvsPatientId, isAppointmentTypeUnset } from '@/utils/appointmentUtils.ts';
import { byIdToRecord } from '@/utils/arrayUtils.ts';
import { dayjs } from '@/utils/dayjsSetup.ts';
import { getInsuranceTypeFromAppointment, getValidInsuranceTypeFromAppointment } from '@/utils/insurance-utils.ts';
import { hasResolvablePatientId } from '@/utils/patientUtils.ts';
import { getTextFromRichTextField } from '@/utils/prismicUtils.ts';

const logger = getLogger(['util', 'form']);

/** The payload of a resolved lookup, or `undefined` when the answer is not known. */
export const getFormsFetchData = <T>(fetch: FormsFetch<T>): T | undefined =>
    fetch.status === FormsFetchStatus.RESOLVED ? fetch.data : undefined;

/**
 * `formatRequiredForms` drops ids absent from the professional's templates, so a set whose ids are all
 * dropped renders empty and reads as "all done" for a set that was not empty.
 */
const areRequestedFormsNameable = (
    required_forms: FormsFetch<FormDataRequiredTemplate[]>,
    professional_forms: FormsFetch<FormTemplate[]>,
    selected_forms: FormTemplateAppointment[] | undefined
): boolean => {
    const requested = selected_forms ?? getFormsFetchData(required_forms);
    if (!requested?.length) {
        return true;
    }

    const templates = getFormsFetchData(professional_forms);
    if (!templates) {
        return false;
    }

    return requested.every(({ id }) => templates.some((template) => template.id === id));
};

/**
 * Whether the required set can be stated. Templates are needed to name any form, so their failure is
 * decisive; a curated selection answers on its own; an unset service type resolves to all of the
 * professional's forms. Only a failure or a missing input leaves the set unknown.
 */
export const getFormsResolution = (
    required_forms: FormsFetch<FormDataRequiredTemplate[]>,
    professional_forms: FormsFetch<FormTemplate[]>,
    selected_forms: FormTemplateAppointment[] | undefined
): FormsResolution => {
    if (professional_forms.status === FormsFetchStatus.FAILED) {
        return FormsResolution.UNRESOLVED;
    }

    if (!areRequestedFormsNameable(required_forms, professional_forms, selected_forms)) {
        return FormsResolution.UNRESOLVED;
    }

    // Empty still answers: the lookup yields `undefined` for a failure and for no record, so `[]` can
    // only mean a stored record saying none are needed, which is how `??` already renders it.
    if (selected_forms) {
        return FormsResolution.RESOLVED;
    }

    if (required_forms.status === FormsFetchStatus.FAILED) {
        return FormsResolution.UNRESOLVED;
    }

    if (
        required_forms.status === FormsFetchStatus.SKIPPED &&
        required_forms.reason !== FormsSkipReason.UNSET_APPOINTMENT_TYPE
    ) {
        return FormsResolution.UNRESOLVED;
    }

    return FormsResolution.RESOLVED;
};

/**
 * Why a request cannot answer the required-forms lookup, or `undefined` when it can, in priority order.
 * An unset service type already has a defined answer; the other three leave the set genuinely unknown.
 */
export const getFormsSkipReason = (
    appointment: DailyAppointment,
    insuranceTypeOverride?: InsuranceType
): FormsSkipReason | undefined => {
    // First: nobody attached means no forms whatever the service maps to. Letting the unset answer win
    // here would render all of the professional's forms for a patient that does not exist.
    if (!hasResolvablePatientId(appointment)) {
        return FormsSkipReason.NO_PATIENT;
    }

    if (isAppointmentTypeUnset(appointment)) {
        return FormsSkipReason.UNSET_APPOINTMENT_TYPE;
    }

    if (appointment.data.services.length === 0) {
        return FormsSkipReason.NO_SERVICE_TYPE;
    }

    if (!(insuranceTypeOverride ?? getValidInsuranceTypeFromAppointment(appointment))) {
        return FormsSkipReason.NO_INSURANCE_TYPE;
    }

    return undefined;
};

/**
 * Formats required forms by merging selected forms, required forms,
 * and professional templates into a unified structure.
 * selected_forms have precedent over required_forms
 **/
export const formatRequiredForms = (
    selected_forms: FormTemplateAppointment[] | undefined,
    appointment_forms: FormDataRequiredTemplate[] | undefined,
    professional_forms: FormTemplate[],
    shared_forms: GetDocumentsResult[] | undefined,
    form_code: FormDataCode | undefined,
    appointment?: Appointment
): FormattedForm[] => {
    // For an unmapped appointment type (service key "unset") the required-forms set is unknown, so default
    // to requiring ALL professional forms — unless staff have explicitly curated a selection for this appointment.
    const explicit_forms = selected_forms ?? appointment_forms;
    const default_all_required = !explicit_forms?.length && !!appointment && isAppointmentTypeUnset(appointment);
    const form_source = default_all_required ? professional_forms : (explicit_forms ?? []);
    const form_ids: string[] = form_source.map(({ id }) => id);
    const formatted_forms: FormattedForm[] = [];

    const professional_forms_map = byIdToRecord(professional_forms);
    const required_forms_map = appointment_forms ? byIdToRecord(appointment_forms) : undefined;

    for (const form_id of form_ids) {
        const professional_form = professional_forms_map[form_id];
        if (!professional_form) continue;

        const required_form = required_forms_map?.[form_id];

        const was_shared: boolean =
            shared_forms?.some(({ context }) => context.template_id === professional_form.id) ?? false;

        const is_filled: boolean = form_code?.data.forms.find((f) => f.id === form_id)?.is_filled ?? false;

        const formatted_form = {
            id: professional_form.id,
            name: getFormNameFromFormattedForm(professional_form),
            template_key: professional_form.template_key, // todo: can this really be undefined??
            is_completed: (required_form?.is_completed ?? false) || was_shared || is_filled,
        };

        formatted_forms.push(formatted_form);
    }

    return formatted_forms.filter((form) => !!form);
};

export const toggleSelectedForm = (current_selected_forms: string[], form_id: string): string[] => {
    const is_already_selected = current_selected_forms.includes(form_id);
    if (is_already_selected) {
        // Remove form_id from the selected forms
        return current_selected_forms.filter((id: string) => id !== form_id);
    } else {
        // Add form_id to the selected forms
        return [...current_selected_forms, form_id];
    }
};

const formatFormToSave = (professional_form_templates: FormTemplate[], form_id: string): FormKeyObject | undefined => {
    const professional_form = professional_form_templates.find(({ id }) => id === form_id);
    if (professional_form) {
        return { id: professional_form.id, key: professional_form.template_key ?? '' };
    } else {
        return undefined;
    }
};

export const filterAndFormatFormsToSave = (
    professional_form_templates: FormTemplate[],
    selected_forms: string[]
): FormKeyObject[] => {
    return selected_forms
        .map((form_id) => formatFormToSave(professional_form_templates, form_id))
        .filter((form) => !!form);
};

export const getDocumentType = (
    medical_records_responses: MedicalRecordsDocumentDataTypeOfDocResponseItem[] | undefined,
    document_type: DocumentType
): string => {
    const found_translation: MedicalRecordsDocumentDataTypeOfDocResponseItem | undefined =
        medical_records_responses?.find((translation) => translation.type_of_doc_responses === document_type);

    return getTextFromRichTextField(found_translation?.type_of_document) || 'Unbekannter Dokumenttyp';
};

export const downloadUrl = (url: string): void => {
    window.open(url, '_blank', 'noreferrer');
};

export const getErrorMessageFromPrismicContent = (
    code: DocumentCodeErrorResponse,
    content: MedicalRecordsDocument['data']
): string => {
    const medical_record = content[code as keyof MedicalRecordsDocument['data']];

    return (
        getTextFromRichTextField(medical_record as RTNode) ??
        'Es ist ein Fehler aufgetreten, bitte versuchen Sie es erneut'
    );
};

export const downloadDocument = async (
    appointment: Appointment,
    document_id: string,
    onNeedToInsertOtpCb: () => void
): Promise<void> => {
    const [success, document_download_data, error] = await getDocumentDownloadUrl(appointment.user_id, document_id);

    if (success && document_download_data && document_download_data.download_url) {
        downloadUrl(document_download_data.download_url);
        return;
    }

    if (error?.status === 403) {
        const error_data = error.data;
        if (
            error_data?.error_code === DocumentCodeErrorResponse.INVALID_OR_EXPIRED ||
            error_data?.error_code === DocumentCodeErrorResponse.EXPIRED_CODE
        ) {
            onNeedToInsertOtpCb();
            return;
        }
    }
    const fallback_error = error?.data ?? error?.message;
    showErrorNotification(`unexpected_error_sending_email: ${JSON.stringify(fallback_error)}`);
};

export const getFormNameFromFormattedForm = (form: FormTemplate): string =>
    (form.is_custom ? form.form_name_de : (form.content as FormTemplateContent).name) ?? '';

export const prepareGenerateCodeBody = (
    location: EnrichedLocation,
    appointment: DailyAppointment,
    forms: FormKeyObject[],
    code?: string
): FormDataCodeDto | undefined => {
    if (forms.length === 0) {
        return undefined;
    }

    // Sanity check
    if (!appointment.patient) {
        logger.error('No patient found in appointment', { appointment: appointment });
        return undefined;
    }

    const { selected_location } = useConfigStore.getState();
    const config = selected_location?.config_record[getInstanceId(appointment) ?? ''];
    const appointmentInsuranceType = getInsuranceTypeFromAppointment(appointment);

    const address = appointment.patient.address;
    const personal_data: PurePersonalData = {
        personal: {
            gender: appointment.patient.name.gender,
            first_name: appointment.patient.name.first_name,
            middle_name: appointment.patient.name.middle_name,
            last_name: appointment.patient.name.last_name,
            date_of_birth: dayjs(appointment.patient.account.date_of_birth).format('YYYY-MM-DD'), // '1977-05-20'
            email: appointment.patient.contact.email,
            phone: appointment.patient.contact.phone,
        },
        // A patient without an address, or with one lacking a country code, gets no contact: the form-data API
        // rejects an empty country code
        ...(address?.country_code && {
            contact: {
                street: address.street ?? '',
                house_number: address.house_number ?? '',
                zip: address.zip ?? '',
                city: address.city ?? '',
                country_code: address.country_code,
            },
        }),
        health_insurance: {
            location_id: '', // TODO: PVS-MIGRATION make it more PVS agnostic !! there is no location_id in data_cur
            insurance_type: appointmentInsuranceType || appointment.patient.insurance?.type || '',
            insurance_company: appointment.patient.insurance?.name ?? '',
        },
    };

    return {
        code: code,
        forms: forms,
        is_user_linked: config?.is_form_code_linking_enabled ?? false,

        location: {
            location_key: location.key,
            dc_instance_id: getInstanceId(appointment),
        },
        user: {
            user_id: appointment.user_id,
            ext_user_id: getPvsPatientId(appointment) ?? '',
            first_name: appointment.patient.name.first_name,
            last_name: appointment.patient.name.last_name,
            date_of_birth: dayjs(appointment.patient.account.date_of_birth).format('YYYY-MM-DD'),
            //
            personal_data: config?.is_form_code_linking_enabled ? personal_data : undefined,
            pvs_provider: config?.pvs_provider,
        },
        professional: {
            professional_id: appointment.professional.id,
            professional_key: appointment.professional.key,
        },
        appointment_id: appointment.id,
    };
};

/** Whether any of street, house number, addon, postal code or city is filled; the country is preselected, so it never counts. */
export const hasAddressParts = (formData: PatientFormData | EditPatientFormData): boolean =>
    [formData.street, formData.house_number, formData.addon, formData.postal_code, formData.city].some((value) =>
        value?.trim()
    );

export const validatePatientForm = (
    formData: PatientFormData | EditPatientFormData,
    pvsProvider: PVSProvider,
    isEdit: boolean = false,
    hasStoredAddress: boolean = false
): {
    show_notification: boolean;
    message?: string;
} => {
    if (!formData.first_name) {
        return {
            show_notification: true,
            message: 'Vorname ist erforderlich',
        };
    }

    if (!formData.last_name) {
        return {
            show_notification: true,
            message: 'Nachname ist erforderlich',
        };
    }

    // Doc Cirrus stores a patient without an address but drops a partial one, so its address is all-or-nothing.
    // Only an edit can keep a patient without an address: a new patient still needs one.
    // A stored address cannot be cleared: the PMS keeps it, so clearing it here would only look successful.
    const isAddressOptional =
        pvsProvider === PVSProvider.DOC_CIRRUS && isEdit && !hasStoredAddress && !hasAddressParts(formData);
    if (pvsProvider === PVSProvider.DOC_CIRRUS && hasStoredAddress && !hasAddressParts(formData)) {
        return {
            show_notification: true,
            message: 'Adresse kann nicht entfernt werden!',
        };
    }
    const addressFields: (string | undefined)[] = [
        formData.country_code,
        formData.street,
        formData.house_number,
        formData.postal_code,
        formData.city,
    ];
    if (!isAddressOptional && addressFields.some((value) => !value?.trim())) {
        return {
            show_notification: true,
            message: 'Adresse ist unvollständig!',
        };
    }

    if (!formData.date_of_birth) {
        return {
            show_notification: true,
            message: 'Geburtsdatum ist erforderlich',
        };
    }

    // Create specific validation for new patients
    if (!isEdit) {
        formData = formData as PatientFormData;
        if (!formData.instance_id) {
            return {
                show_notification: true,
                message: 'Instanz ist erforderlich',
            };
        }

        if (!formData.location_id && pvsProvider === PVSProvider.DOC_CIRRUS) {
            return {
                show_notification: true,
                message: 'Betriebsstätte ist erforderlich',
            };
        }
    }

    return {
        show_notification: false,
    };
};

export const hasFormsToFill = (forms: FormattedForm[] | undefined): boolean => {
    return forms?.some((form) => !form.is_completed) ?? false;
};

export const triggerFormSubmit = (): void => {
    useFormStore.getState().form_element?.dispatchEvent(
        new Event('submit', {
            bubbles: true,
            cancelable: true,
        })
    );
};
